import { useAuth } from "@clerk/react"
import {
  Copy,
  Dices,
  Download,
  MoreHorizontal,
  Pencil,
  Star,
  Trash2,
  Upload,
  X,
} from "lucide-react"
import { useCallback, useEffect, useMemo, useState } from "react"
import { isRateLimitError } from "../api/rateLimit"
import { useIdbState, useUserIdbState } from "../hooks/useIdbState"
import {
  getAllFactionSearchValues,
  getAllSkillSearchValues,
  getFactionName,
  getFactionSearchValues,
  getSkillName,
  getSkillSearchValues,
} from "../i18n/gameContent"
import { useI18n } from "../i18n/useI18n"
import { isLibrarySyncUnavailable } from "../library/errors"
import {
  LibraryRefreshStatus,
  RecordOriginBadge,
} from "../library/LibrarySyncIndicators"
import { mergeAuthoritativeRemoteRecords } from "../library/merge"
import {
  useBulkDeleteCardsMutation,
  useBulkSyncCardsMutation,
  useDeleteCardMutation,
  usePullCardMutation,
  useSyncCardMutation,
} from "../library/query"
import { fetchManagedCardRecordsFromServer } from "../library/remote"
import {
  getRemoteSyncState,
  markRecordSyncFailed,
  markRecordSyncPending,
  markRecordSyncSucceeded,
  shouldAutoRetrySync,
} from "../library/syncState"
import { FilterValueButton } from "../manage/FilterValueButton"
import { ManagementFilterBar } from "../manage/ManagementFilterBar"
import type { TableFilterField } from "../manage/tableFilters"
import { useTableFilter } from "../manage/tableFilters"
import { useLibraryTransfer } from "../manage/useLibraryTransfer"
import { SKILL_MAP } from "../match/engine/content/skills"
import {
  ARTWORK_ASSET_STORAGE_KEY,
  ARTWORK_ASSET_STORAGE_VERSION,
  ArtworkAssetStoreSchema,
  EMPTY_ARTWORK_ASSET_STORE,
} from "../media/assets"
import { useSessionQuery } from "../query/authenticated"
import { queryKeys } from "../query/keys"
import type {
  CardColor,
  ManagedCardRecord,
  SkillId,
  SkillSelection,
} from "../schemas"
import { ArtworkThumb } from "./ArtworkThumb"
import { CardEditorModal } from "./CardEditorModal"
import { CostBadge } from "./CostBadge"
import { FACTION_PALETTE } from "./cardTheme"
import { randomizeCard } from "./randomize"
import {
  AllyIcon,
  AttackIcon,
  DoubleAllyIcon,
  ExhaustIcon,
  FACTION_ICON,
  HealthIcon,
  SKILL_ICON,
} from "./SkillIcon"
import {
  buildDuplicatedCardRecord,
  CARD_LIBRARY_STORAGE_KEY,
  CARD_LIBRARY_STORAGE_VERSION,
  CardLibraryStateSchema,
  EMPTY_CARD_LIBRARY,
} from "./storage"

type SortColumn =
  | "name"
  | "updatedAt"
  | "derivedCost"
  | "attack"
  | "health"
  | "primaryColor"
  | "deckUsageCount"

type SortDirection = "asc" | "desc"

type SortState = {
  column: SortColumn
  direction: SortDirection
}

const DEFAULT_SORT_STATE: SortState = {
  column: "updatedAt",
  direction: "desc",
}

function formatRelativeDate(dateString: string, locale = "en-US"): string {
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
  }).format(new Date(dateString))
}

function getSortedCards(
  cards: readonly ManagedCardRecord[],
  sort: SortState,
): ManagedCardRecord[] {
  return [...cards].sort((left, right) => {
    const leftValue = getSortValue(left, sort.column)
    const rightValue = getSortValue(right, sort.column)

    if (leftValue < rightValue) {
      return sort.direction === "asc" ? -1 : 1
    }

    if (leftValue > rightValue) {
      return sort.direction === "asc" ? 1 : -1
    }

    return left.card.name.localeCompare(right.card.name)
  })
}

function getSortValue(
  record: ManagedCardRecord,
  column: SortColumn,
): number | string {
  switch (column) {
    case "name":
      return record.card.name.toLowerCase()
    case "updatedAt":
      return record.updatedAt
    case "derivedCost":
      return record.card.derivedCost ?? 0
    case "attack":
      return record.card.stats.attack
    case "health":
      return record.card.stats.health
    case "primaryColor":
      return record.card.primaryColor
    case "deckUsageCount":
      return record.deckUsageCount
  }
}

function skillAffinityColor(skillId: SkillId, primary: CardColor): string {
  const def = SKILL_MAP.get(skillId)
  if (!def || def.isColorless) return FACTION_PALETTE.colorless.base
  return (
    FACTION_PALETTE[def.affinity as CardColor]?.base ??
    FACTION_PALETTE[primary].base
  )
}

function FactionLabel({ color }: { color: CardColor }) {
  const { t } = useI18n()
  const Icon = FACTION_ICON[color]
  return (
    <span
      className="faction-token"
      style={{ color: FACTION_PALETTE[color].base }}
    >
      <Icon size={14} strokeWidth={2} />
      <span>{getFactionName(t, color)}</span>
    </span>
  )
}

function StatCell({
  kind,
  value,
}: {
  kind: "attack" | "health"
  value: number
}) {
  const Icon = kind === "attack" ? AttackIcon : HealthIcon
  return (
    <span className={`inline-stat inline-stat-${kind}`}>
      <Icon size={14} strokeWidth={2} />
      <span>{value}</span>
    </span>
  )
}

function SkillModifierBadges({ selection }: { selection: SkillSelection }) {
  const { t } = useI18n()
  return (
    <span className="skill-summary-modifiers">
      {selection.mode === "non_exhausting" && (
        <span
          className="skill-modifier-badge"
          title={t("cardFace.skill.nonExhausting")}
        >
          <Star size={11} strokeWidth={2} />
        </span>
      )}
      {selection.mode === "self_destruct" && (
        <span
          className="skill-modifier-badge"
          title={t("cardFace.skill.overload")}
        >
          <X size={11} strokeWidth={2} />
        </span>
      )}
      {selection.allyRequirement === 1 && (
        <span
          className="skill-modifier-badge"
          title={t("cardFace.skill.ally.one")}
        >
          <AllyIcon size={11} strokeWidth={2} />
        </span>
      )}
      {selection.allyRequirement === 2 && (
        <span
          className="skill-modifier-badge"
          title={t("cardFace.skill.ally.two")}
        >
          <DoubleAllyIcon size={11} strokeWidth={2} />
        </span>
      )}
      {selection.mode == null &&
        SKILL_MAP.get(selection.skillId)?.isPassive === false && (
          <span
            className="skill-modifier-badge"
            title={t("cardFace.skill.exhaust")}
          >
            <ExhaustIcon size={11} strokeWidth={2} />
          </span>
        )}
    </span>
  )
}

function SkillSummary({
  onAppendFilter,
  selection,
  primary,
}: {
  onAppendFilter: (
    key: string,
    value: string,
    options?: { negate?: boolean },
  ) => void
  selection: SkillSelection
  primary: CardColor
}) {
  const { t } = useI18n()
  const Icon = SKILL_ICON[selection.skillId] ?? ExhaustIcon
  const skillName = getSkillName(t, selection.skillId)
  return (
    <li className="skill-summary-item">
      <FilterValueButton
        className="table-filter-link skill-summary-filter-link"
        filterKey="skill"
        filterValue={skillName}
        onAppendFilter={onAppendFilter}
      >
        <span
          className="skill-summary-icon"
          style={{ color: skillAffinityColor(selection.skillId, primary) }}
        >
          <Icon size={14} strokeWidth={2} />
        </span>
        <span className="skill-summary-text">
          <span className="skill-summary-label">{skillName}</span>
          <SkillModifierBadges selection={selection} />
        </span>
      </FilterValueButton>
    </li>
  )
}

export function CardLibraryPage() {
  const { locale, t } = useI18n()
  const { userId } = useAuth()
  const [cards, , setCards] = useUserIdbState(
    CARD_LIBRARY_STORAGE_KEY,
    CardLibraryStateSchema,
    {
      defaultValue: EMPTY_CARD_LIBRARY,
      version: CARD_LIBRARY_STORAGE_VERSION,
    },
  )
  const [artworkAssets, , setArtworkAssets] = useIdbState(
    ARTWORK_ASSET_STORAGE_KEY,
    ArtworkAssetStoreSchema,
    {
      defaultValue: EMPTY_ARTWORK_ASSET_STORE,
      version: ARTWORK_ASSET_STORAGE_VERSION,
    },
  )
  const [selectedCardIds, setSelectedCardIds] = useState<string[]>([])
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT_STATE)
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)
  const [pullingCardIds, setPullingCardIds] = useState<string[]>([])
  const [remoteRefreshFailed, setRemoteRefreshFailed] = useState(false)
  const [remoteRefreshedAt, setRemoteRefreshedAt] = useState<string | null>(
    null,
  )
  const [syncingCardIds, setSyncingCardIds] = useState<string[]>([])
  const [editorOpen, setEditorOpen] = useState(false)
  const [editingRecord, setEditingRecord] = useState<ManagedCardRecord | null>(
    null,
  )
  const cardTransfer = useLibraryTransfer({
    artworkAssets,
    getId: (record) => record.card.id,
    kind: "cards",
    label: t("table.cards.title"),
    records: cards,
    setArtworkAssets,
    setRecords: setCards,
  })
  const cardFilterFields = useMemo<
    readonly TableFilterField<ManagedCardRecord>[]
  >(() => {
    return [
      {
        key: "name",
        getValues: (record) => [record.card.name],
      },
      {
        key: "id",
        aliases: ["card"],
        getValues: (record) => [record.card.id],
      },
      {
        key: "kind",
        getValues: (record) => [record.card.kind],
        getSuggestions: () => [
          "starter",
          "authored_unit",
          "better_battle_bot",
          "hero",
        ],
      },
      {
        key: "faction",
        aliases: ["color"],
        getValues: (record) => [
          ...getFactionSearchValues(t, record.card.primaryColor),
          ...(record.card.secondaryColor
            ? getFactionSearchValues(t, record.card.secondaryColor)
            : []),
        ],
        getSuggestions: () => getAllFactionSearchValues(t),
      },
      {
        key: "primary",
        getValues: (record) =>
          getFactionSearchValues(t, record.card.primaryColor),
        getSuggestions: () => getAllFactionSearchValues(t),
      },
      {
        key: "secondary",
        getValues: (record) =>
          record.card.secondaryColor
            ? getFactionSearchValues(t, record.card.secondaryColor)
            : [],
        getSuggestions: () => getAllFactionSearchValues(t),
      },
      {
        key: "attack",
        aliases: ["atk"],
        type: "number",
        getValues: (record) => [record.card.stats.attack],
      },
      {
        key: "health",
        aliases: ["hp"],
        type: "number",
        getValues: (record) => [record.card.stats.health],
      },
      {
        key: "cost",
        type: "number",
        getValues: (record) => [record.card.derivedCost],
      },
      {
        key: "skill",
        aliases: ["skills"],
        getValues: (record) =>
          record.card.selectedSkills.flatMap((selection) =>
            getSkillSearchValues(t, selection.skillId),
          ),
        getSuggestions: () => getAllSkillSearchValues(t),
      },
      {
        key: "decks",
        aliases: ["usage"],
        type: "number",
        getValues: (record) => [record.deckUsageCount],
      },
      {
        key: "tag",
        aliases: ["tags"],
        getValues: (record) => record.card.tags ?? [],
      },
      {
        key: "updated",
        getValues: (record) => [
          record.updatedAt,
          formatRelativeDate(record.updatedAt, locale),
        ],
      },
    ]
  }, [locale, t])

  const cardFilter = useTableFilter({
    records: cards,
    fields: cardFilterFields,
  })

  const sortedCards = useMemo(
    () => getSortedCards(cardFilter.filteredRecords, sort),
    [cardFilter.filteredRecords, sort],
  )

  const syncCardMutation = useSyncCardMutation()
  const deleteCardMutation = useDeleteCardMutation()
  const pullCardMutation = usePullCardMutation()
  const bulkSyncCardsMutation = useBulkSyncCardsMutation()
  const bulkDeleteCardsMutation = useBulkDeleteCardsMutation()

  const remoteCardsQuery = useSessionQuery({
    enabled: Boolean(userId),
    queryFn: ({ sessionToken, signal }) =>
      fetchManagedCardRecordsFromServer(sessionToken, signal),
    queryKey: queryKeys.library.cards(userId),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  })

  useEffect(() => {
    if (!remoteCardsQuery.data) {
      return
    }

    setCards((previous) =>
      mergeAuthoritativeRemoteRecords(
        previous,
        remoteCardsQuery.data,
        (record) => record.card.id,
      ),
    )
    setRemoteRefreshFailed(false)
    setRemoteRefreshedAt(new Date().toISOString())
  }, [remoteCardsQuery.data, setCards])

  useEffect(() => {
    if (!remoteCardsQuery.isError) {
      return
    }
    // Keep local authoring usable when the worker sync path is not configured yet.
    setRemoteRefreshFailed(true)
  }, [remoteCardsQuery.isError])
  const hasSelection = selectedCardIds.length > 0
  const selectedCardRecords = cards.filter((record) =>
    selectedCardIds.includes(record.card.id),
  )

  const toggleSelection = (cardId: string) => {
    setSelectedCardIds((previous) =>
      previous.includes(cardId)
        ? previous.filter((id) => id !== cardId)
        : [...previous, cardId],
    )
  }

  const toggleSelectAll = () => {
    setSelectedCardIds((previous) =>
      sortedCards.every((record) => previous.includes(record.card.id))
        ? previous.filter(
            (id) => !sortedCards.some((record) => record.card.id === id),
          )
        : [
            ...new Set([
              ...previous,
              ...sortedCards.map((record) => record.card.id),
            ]),
          ],
    )
  }

  const updateSort = (column: SortColumn) => {
    setSort((previous) =>
      previous.column === column
        ? {
            column,
            direction: previous.direction === "asc" ? "desc" : "asc",
          }
        : {
            column,
            direction:
              column === "name" || column === "primaryColor" ? "asc" : "desc",
          },
    )
  }

  const handleDelete = (cardId: string) => {
    setCards((previous) => {
      const next = previous.filter((record) => record.card.id !== cardId)
      purgeOrphanedArtwork(previous, next)
      return next
    })
    setSelectedCardIds((previous) => previous.filter((id) => id !== cardId))
    setPendingDeleteId(null)
    if (!userId) return

    void deleteCardMutation.mutateAsync(cardId).catch(() => {
      // Deletion stays local-first when the worker sync path is unavailable.
    })
  }

  const handleBulkDelete = () => {
    const deletedIds = new Set(selectedCardIds)
    setCards((previous) => {
      const next = previous.filter((record) => !deletedIds.has(record.card.id))
      purgeOrphanedArtwork(previous, next)
      return next
    })
    setSelectedCardIds([])
    setPendingDeleteId(null)
    if (!userId) return

    void bulkDeleteCardsMutation.mutateAsync([...deletedIds]).catch(() => {
      // Bulk deletion stays local-first when the worker sync path is unavailable.
    })
  }

  /** Remove artwork assets no longer referenced by any card in `next`. */
  const purgeOrphanedArtwork = (
    previous: readonly ManagedCardRecord[],
    next: readonly ManagedCardRecord[],
  ) => {
    // Collect assetIds that were removed (referenced by previous but not next)
    const remainingIds = new Set(
      next.flatMap((r) => r.card.artwork?.variants.map((v) => v.assetId) ?? []),
    )
    const hadArtwork = previous.some((r) =>
      r.card.artwork?.variants.some((v) => !remainingIds.has(v.assetId)),
    )
    if (!hadArtwork) return
    setArtworkAssets((assets) =>
      assets.filter((asset) => remainingIds.has(asset.assetId)),
    )
  }

  const handleDuplicate = (record: ManagedCardRecord) => {
    setCards((previous) => {
      return [buildDuplicatedCardRecord(record, previous), ...previous]
    })
  }

  const handleSyncCard = useCallback(
    (record: ManagedCardRecord) => {
      if (!userId || syncingCardIds.includes(record.card.id)) return

      setSyncingCardIds((previous) => [...previous, record.card.id])
      void syncCardMutation
        .mutateAsync(record)
        .then((synced) => {
          setCards((previous) =>
            previous.map((entry) =>
              entry.card.id === synced.card.id
                ? markRecordSyncSucceeded(synced)
                : entry,
            ),
          )
        })
        .catch((error) => {
          setCards((previous) =>
            previous.map((entry) =>
              entry.card.id !== record.card.id
                ? entry
                : isLibrarySyncUnavailable(error) || isRateLimitError(error)
                  ? entry
                  : markRecordSyncFailed(entry),
            ),
          )
        })
        .finally(() => {
          setSyncingCardIds((previous) =>
            previous.filter((cardId) => cardId !== record.card.id),
          )
        })
    },
    [setCards, syncCardMutation, syncingCardIds, userId],
  )

  const handlePullRemoteCard = (record: ManagedCardRecord) => {
    if (!userId || pullingCardIds.includes(record.card.id)) return

    setPullingCardIds((previous) => [...previous, record.card.id])
    void pullCardMutation
      .mutateAsync(record.card.id)
      .then((remoteRecord) => {
        setCards((previous) =>
          previous.map((entry) =>
            entry.card.id === remoteRecord.card.id
              ? markRecordSyncSucceeded(remoteRecord)
              : entry,
          ),
        )
      })
      .catch(() => {
        // Leave the local card in conflict state if remote recovery fails.
      })
      .finally(() => {
        setPullingCardIds((previous) =>
          previous.filter((cardId) => cardId !== record.card.id),
        )
      })
  }

  const handleBulkSync = () => {
    if (!userId) return

    const recordsToSync = cards.filter(
      (record) =>
        selectedCardIds.includes(record.card.id) &&
        !record.remoteSyncedAt &&
        !syncingCardIds.includes(record.card.id),
    )
    if (recordsToSync.length === 0) return

    setSyncingCardIds((previous) => [
      ...previous,
      ...recordsToSync.map((record) => record.card.id),
    ])

    void bulkSyncCardsMutation
      .mutateAsync(recordsToSync)
      .then(({ results }) => {
        const syncedById = new Map<string, ManagedCardRecord>()
        const failedIds = new Set<string>()

        for (const [index, result] of results.entries()) {
          const source = recordsToSync[index]
          if (!source) continue
          if (result.status === "synced") {
            syncedById.set(
              source.card.id,
              markRecordSyncSucceeded(result.record),
            )
          } else if (result.status === "conflict") {
            syncedById.set(source.card.id, source)
          } else {
            failedIds.add(source.card.id)
          }
        }

        setCards((previous) =>
          previous.map((entry) => {
            const synced = syncedById.get(entry.card.id)
            if (synced) return synced
            if (failedIds.has(entry.card.id)) {
              return markRecordSyncFailed(entry)
            }
            return entry
          }),
        )
      })
      .catch(() => {
        // Bulk sync stays local-first when the worker sync path is unavailable.
      })
      .finally(() => {
        setSyncingCardIds((previous) =>
          previous.filter(
            (cardId) =>
              !recordsToSync.some((record) => record.card.id === cardId),
          ),
        )
      })
  }

  const handleBulkRandomize = () => {
    const toRandomize = new Set(selectedCardIds)
    const now = new Date().toISOString()

    const randomizedRecords: ManagedCardRecord[] = []
    setCards((previous) =>
      previous.map((record) => {
        if (!toRandomize.has(record.card.id)) return record
        const nextRecord: ManagedCardRecord = {
          ...record,
          card: randomizeCard(record.card),
          updatedAt: now,
          remoteSyncedAt: undefined,
          remoteSyncFailedAt: undefined,
          remoteSyncConflictAt: undefined,
        }
        randomizedRecords.push(nextRecord)
        return nextRecord
      }),
    )
    setSelectedCardIds([])

    if (!userId) return

    setSyncingCardIds((previous) => [
      ...previous,
      ...randomizedRecords.map((record) => record.card.id),
    ])

    void bulkSyncCardsMutation
      .mutateAsync(randomizedRecords)
      .then(({ results }) => {
        const syncedById = new Map<string, ManagedCardRecord>()
        const failedIds = new Set<string>()

        for (const [index, result] of results.entries()) {
          const source = randomizedRecords[index]
          if (!source) continue
          if (result.status === "synced") {
            syncedById.set(
              source.card.id,
              markRecordSyncSucceeded(result.record),
            )
          } else if (result.status === "conflict") {
            syncedById.set(source.card.id, source)
          } else {
            failedIds.add(source.card.id)
          }
        }

        setCards((previous) =>
          previous.map((entry) => {
            const synced = syncedById.get(entry.card.id)
            if (synced) return synced
            if (failedIds.has(entry.card.id)) return markRecordSyncFailed(entry)
            return entry
          }),
        )
      })
      .catch(() => {
        // Stays local-first when the worker sync path is unavailable.
      })
      .finally(() => {
        setSyncingCardIds((previous) =>
          previous.filter(
            (cardId) =>
              !randomizedRecords.some((record) => record.card.id === cardId),
          ),
        )
      })
  }

  useEffect(() => {
    if (!userId) return
    if (typeof navigator !== "undefined" && navigator.onLine === false) return

    const nextRecord = cards.find(
      (record) =>
        !syncingCardIds.includes(record.card.id) && shouldAutoRetrySync(record),
    )

    if (!nextRecord) return
    handleSyncCard(markRecordSyncPending(nextRecord))
  }, [cards, handleSyncCard, syncingCardIds, userId])

  const openCreate = () => {
    setEditingRecord(null)
    setEditorOpen(true)
  }

  const openEdit = (record: ManagedCardRecord) => {
    setEditingRecord(record)
    setEditorOpen(true)
  }

  const handleEditorSave = (saved: ManagedCardRecord) => {
    setCards((previous) => {
      const idx = previous.findIndex((r) => r.card.id === saved.card.id)
      if (idx >= 0) {
        const next = [...previous]
        next[idx] = saved
        return next
      }
      return [saved, ...previous]
    })
    setEditorOpen(false)
  }

  const sortArrow = (column: SortColumn) => {
    if (sort.column !== column) {
      return null
    }

    return sort.direction === "asc" ? " ^" : " v"
  }

  return (
    <section className="panel management-panel">
      <CardEditorModal
        editingRecord={editingRecord}
        onClose={() => setEditorOpen(false)}
        onSave={handleEditorSave}
        open={editorOpen}
      />
      <div className="page-header">
        <div>
          <p className="eyebrow">{t("table.cards.eyebrow")}</p>
          <h2>{t("table.cards.title")}</h2>
          <p className="lede table-lede">{t("table.cards.lede")}</p>
        </div>
        <button className="primary-button" onClick={openCreate} type="button">
          {t("table.cards.create")}
        </button>
      </div>

      <ManagementFilterBar
        filter={cardFilter}
        placeholder={t("filter.placeholder.cards")}
      />
      <p className="table-transfer-hint">{t("table.transfer.hint")}</p>

      {cardTransfer.status ? (
        <output
          className={`table-transfer-status table-transfer-status-${cardTransfer.status.tone}`}
        >
          {cardTransfer.status.message}
        </output>
      ) : null}
      <LibraryRefreshStatus
        failed={remoteRefreshFailed}
        lastRefreshedAt={remoteRefreshedAt}
      />

      <section
        aria-label={t("table.cards.title")}
        aria-busy={cardTransfer.isBusy}
        className={`table-frame${cardTransfer.isDragActive ? " table-frame-drop-active" : ""}`}
        onDragEnter={cardTransfer.onDragEnter}
        onDragLeave={cardTransfer.onDragLeave}
        onDragOver={cardTransfer.onDragOver}
        onDrop={cardTransfer.onDrop}
      >
        {cardTransfer.isDragActive ? (
          <div className="table-drop-zone">
            <strong>
              {t("table.transfer.dropTitle", { table: t("table.cards.title") })}
            </strong>
            <span>{t("table.transfer.dropBody")}</span>
          </div>
        ) : null}
        {hasSelection ? (
          <div className="bulk-bar">
            <button
              aria-label={t("table.bulk.clearSelection")}
              className="bulk-bar-dismiss"
              onClick={() => setSelectedCardIds([])}
              type="button"
            >
              X
            </button>
            <strong>
              {t("common.selectedCount", { count: selectedCardIds.length })}
            </strong>
            <div className="bulk-actions">
              <button
                className="secondary-button"
                disabled={
                  cardTransfer.isBusy || selectedCardRecords.length === 0
                }
                onClick={() =>
                  void cardTransfer.downloadBackup(selectedCardRecords)
                }
                type="button"
              >
                {t("table.transfer.download")}
              </button>
              <button
                className="secondary-button"
                disabled={
                  !userId ||
                  !selectedCardIds.some((cardId) => {
                    const record = cards.find(
                      (entry) => entry.card.id === cardId,
                    )
                    return record != null && !record.remoteSyncedAt
                  })
                }
                onClick={handleBulkSync}
                type="button"
              >
                {t("table.bulk.syncSelection")}
              </button>
              <button
                className="secondary-button"
                onClick={handleBulkRandomize}
                title={t("table.bulk.randomize")}
                type="button"
              >
                <Dices size={14} strokeWidth={2} />
                {t("table.bulk.randomize")}
              </button>
              <button
                className="danger-button"
                onClick={handleBulkDelete}
                type="button"
              >
                {t("common.delete")}
              </button>
            </div>
          </div>
        ) : null}
        <table className="management-table">
          <thead>
            <tr>
              <th className="sticky-column sticky-column-first" scope="col">
                <input
                  aria-label={t("table.cards.selectAll")}
                  checked={
                    sortedCards.length > 0 &&
                    sortedCards.every((record) =>
                      selectedCardIds.includes(record.card.id),
                    )
                  }
                  onChange={toggleSelectAll}
                  type="checkbox"
                />
              </th>
              <th className="sticky-column sticky-column-name" scope="col">
                <button
                  className="sort-button"
                  onClick={() => updateSort("name")}
                  type="button"
                >
                  {t("table.column.name")}
                  {sortArrow("name")}
                </button>
              </th>
              <th scope="col">
                <button
                  className="sort-button"
                  onClick={() => updateSort("primaryColor")}
                  type="button"
                >
                  {t("common.faction")}
                  {sortArrow("primaryColor")}
                </button>
              </th>
              <th scope="col">
                <button
                  className="sort-button"
                  onClick={() => updateSort("attack")}
                  type="button"
                >
                  {t("common.attack")}
                  {sortArrow("attack")}
                </button>
              </th>
              <th scope="col">
                <button
                  className="sort-button"
                  onClick={() => updateSort("health")}
                  type="button"
                >
                  {t("common.health")}
                  {sortArrow("health")}
                </button>
              </th>
              <th scope="col">
                <button
                  className="sort-button"
                  onClick={() => updateSort("derivedCost")}
                  type="button"
                >
                  Cost
                  {sortArrow("derivedCost")}
                </button>
              </th>
              <th scope="col">{t("common.skills")}</th>
              <th scope="col">
                <button
                  className="sort-button"
                  onClick={() => updateSort("deckUsageCount")}
                  type="button"
                >
                  {t("common.decks")}
                  {sortArrow("deckUsageCount")}
                </button>
              </th>
              <th scope="col">
                <button
                  className="sort-button"
                  onClick={() => updateSort("updatedAt")}
                  type="button"
                >
                  {t("common.updated")}
                </button>
              </th>
              <th className="sticky-column sticky-column-last" scope="col">
                {t("table.column.actions")}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedCards.map((record) => {
              const isSelected = selectedCardIds.includes(record.card.id)
              const isDeletePending = pendingDeleteId === record.card.id
              const isPulling = pullingCardIds.includes(record.card.id)
              const isSyncing = syncingCardIds.includes(record.card.id)
              const syncState = getRemoteSyncState(record)
              const canSync = Boolean(userId) && syncState !== "synced"
              const canPullRemote = Boolean(userId) && syncState === "conflict"

              return (
                <tr key={record.card.id} data-selected={isSelected}>
                  <td className="sticky-column sticky-column-first">
                    <input
                      aria-label={t("table.row.select", {
                        name: record.card.name,
                      })}
                      checked={isSelected}
                      onChange={() => toggleSelection(record.card.id)}
                      type="checkbox"
                    />
                  </td>
                  <td className="sticky-column sticky-column-name">
                    <div className="identity-cell">
                      <div className="identity-cell-main">
                        <ArtworkThumb
                          artwork={record.card.artwork}
                          name={record.card.name}
                          primaryColor={record.card.primaryColor}
                          size="m"
                        />
                        <FilterValueButton
                          filterKey="name"
                          filterValue={record.card.name}
                          onAppendFilter={cardFilter.appendFilterValue}
                        >
                          <strong>{record.card.name}</strong>
                        </FilterValueButton>
                        <RecordOriginBadge record={record} />
                      </div>
                    </div>
                  </td>
                  <td className="faction-cell">
                    <div className="faction-list">
                      <FilterValueButton
                        filterKey="faction"
                        filterValue={getFactionName(
                          t,
                          record.card.primaryColor,
                        )}
                        onAppendFilter={cardFilter.appendFilterValue}
                      >
                        <FactionLabel color={record.card.primaryColor} />
                      </FilterValueButton>
                      {record.card.secondaryColor ? (
                        <FilterValueButton
                          filterKey="faction"
                          filterValue={getFactionName(
                            t,
                            record.card.secondaryColor,
                          )}
                          onAppendFilter={cardFilter.appendFilterValue}
                        >
                          <FactionLabel color={record.card.secondaryColor} />
                        </FilterValueButton>
                      ) : null}
                    </div>
                  </td>
                  <td>
                    <FilterValueButton
                      filterKey="attack"
                      filterValue={String(record.card.stats.attack)}
                      onAppendFilter={cardFilter.appendFilterValue}
                    >
                      <StatCell
                        kind="attack"
                        value={record.card.stats.attack}
                      />
                    </FilterValueButton>
                  </td>
                  <td>
                    <FilterValueButton
                      filterKey="health"
                      filterValue={String(record.card.stats.health)}
                      onAppendFilter={cardFilter.appendFilterValue}
                    >
                      <StatCell
                        kind="health"
                        value={record.card.stats.health}
                      />
                    </FilterValueButton>
                  </td>
                  <td>
                    {record.card.derivedCost != null ? (
                      <FilterValueButton
                        filterKey="cost"
                        filterValue={String(record.card.derivedCost)}
                        onAppendFilter={cardFilter.appendFilterValue}
                      >
                        <CostBadge
                          cost={record.card.derivedCost}
                          size="s"
                          variant="subtle"
                        />
                      </FilterValueButton>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>
                    {record.card.selectedSkills.length > 0 ? (
                      <ul className="skill-summary-list">
                        {record.card.selectedSkills.map((selection) => (
                          <SkillSummary
                            key={`${record.card.id}-${selection.skillId}`}
                            onAppendFilter={cardFilter.appendFilterValue}
                            primary={record.card.primaryColor}
                            selection={selection}
                          />
                        ))}
                      </ul>
                    ) : (
                      <span className="muted-cell">{t("common.none")}</span>
                    )}
                  </td>
                  <td>
                    <FilterValueButton
                      filterKey="decks"
                      filterValue={String(record.deckUsageCount)}
                      onAppendFilter={cardFilter.appendFilterValue}
                    >
                      {record.deckUsageCount}
                    </FilterValueButton>
                  </td>
                  <td>{formatRelativeDate(record.updatedAt, locale)}</td>
                  <td className="sticky-column sticky-column-last">
                    <div className="row-actions row-actions-desktop">
                      {canSync ? (
                        <button
                          aria-label={t("table.rowAction.sync", {
                            name: record.card.name,
                          })}
                          className="table-action-button"
                          disabled={isSyncing}
                          onClick={() =>
                            handleSyncCard(markRecordSyncPending(record))
                          }
                          title={t("table.rowAction.sync", {
                            name: record.card.name,
                          })}
                          type="button"
                        >
                          <Upload size={16} strokeWidth={2} />
                        </button>
                      ) : null}
                      {canPullRemote ? (
                        <button
                          aria-label={t("table.rowAction.pullRemote", {
                            name: record.card.name,
                          })}
                          className="table-action-button"
                          disabled={isPulling}
                          onClick={() => handlePullRemoteCard(record)}
                          title={t("library.error.syncConflict")}
                          type="button"
                        >
                          <Download size={16} strokeWidth={2} />
                        </button>
                      ) : null}
                      <button
                        aria-label={t("table.rowAction.edit", {
                          name: record.card.name,
                        })}
                        className="table-action-button"
                        onClick={() => openEdit(record)}
                        title={t("common.edit")}
                        type="button"
                      >
                        <Pencil size={16} strokeWidth={2} />
                      </button>
                      <button
                        aria-label={t("table.rowAction.duplicate", {
                          name: record.card.name,
                        })}
                        className="table-action-button"
                        onClick={() => handleDuplicate(record)}
                        title={t("common.duplicate")}
                        type="button"
                      >
                        <Copy size={16} strokeWidth={2} />
                      </button>
                      {isDeletePending ? (
                        <span className="inline-confirm">
                          <button
                            aria-label={t("table.rowAction.confirmDelete", {
                              name: record.card.name,
                            })}
                            className="table-action-button table-action-button-danger"
                            onClick={() => handleDelete(record.card.id)}
                            title={t("common.confirmDelete")}
                            type="button"
                          >
                            <Trash2 size={16} strokeWidth={2} />
                          </button>
                          <button
                            aria-label={t("table.rowAction.cancelDelete", {
                              name: record.card.name,
                            })}
                            className="table-action-button"
                            onClick={() => setPendingDeleteId(null)}
                            title={t("common.cancel")}
                            type="button"
                          >
                            <X size={16} strokeWidth={2} />
                          </button>
                        </span>
                      ) : (
                        <button
                          aria-label={t("table.rowAction.delete", {
                            name: record.card.name,
                          })}
                          className="table-action-button table-action-button-danger"
                          onClick={() => setPendingDeleteId(record.card.id)}
                          title={t("common.delete")}
                          type="button"
                        >
                          <Trash2 size={16} strokeWidth={2} />
                        </button>
                      )}
                    </div>

                    <details className="row-actions-menu">
                      <summary
                        aria-label={t("table.row.openActions", {
                          name: record.card.name,
                        })}
                      >
                        <MoreHorizontal size={16} strokeWidth={2} />
                      </summary>
                      <div className="context-menu">
                        {canSync ? (
                          <button
                            disabled={isSyncing}
                            onClick={() =>
                              handleSyncCard(markRecordSyncPending(record))
                            }
                            type="button"
                          >
                            {t("table.rowAction.sync", {
                              name: record.card.name,
                            })}
                          </button>
                        ) : null}
                        <button onClick={() => openEdit(record)} type="button">
                          {t("common.edit")}
                        </button>
                        <button
                          onClick={() => handleDuplicate(record)}
                          type="button"
                        >
                          {t("common.duplicate")}
                        </button>
                        <button
                          onClick={() => handleDelete(record.card.id)}
                          type="button"
                        >
                          {t("common.delete")}
                        </button>
                      </div>
                    </details>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </section>
    </section>
  )
}
